export function audioMimeOf(result) {
  return result?.audio_mime || result?.mime || 'audio/wav';
}

export function blobFromBase64(b64, mime = 'audio/wav') {
  const raw = String(b64 || '');
  if (!raw) return null;
  const binary = atob(raw);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime || 'audio/wav' });
}

export function browserVoiceLang(lang) {
  const code = String(lang || 'en').toLowerCase();
  if (code.startsWith('es')) return 'es-ES';
  if (code.startsWith('hi')) return 'hi-IN';
  return 'en-US';
}

export function pickBrowserVoice(voices, lang) {
  const wanted = browserVoiceLang(lang).toLowerCase();
  const prefix = wanted.slice(0, 2);
  const list = Array.isArray(voices) ? voices : [];
  const score = (voice) => {
    const tag = String(voice.lang || '').toLowerCase().replace('_', '-');
    const name = `${voice.name || ''} ${voice.lang || ''}`.toLowerCase();
    
    // Voice must match the prefix of the desired language (e.g., 'en', 'es', 'hi')
    if (!tag.startsWith(prefix)) return 0;
    
    let points = 1; // Base score for matching prefix
    
    // Perfect match for requested locale (e.g., 'en-US' or 'hi-IN')
    if (tag === wanted) points += 2;
    
    // Prioritize female/polite assistant voices
    const isFemale = /female|google|natural|helena|sabina|monica|zira|samantha|kalpana|heera|hazel|susan|lisa|karen|moira|tessa|veena|neerja/i.test(name);
    if (isFemale) points += 4;
    
    return points;
  };
  return list
    .map((voice) => ({ voice, n: score(voice) }))
    .filter((item) => item.n > 0)
    .sort((a, b) => b.n - a.n)[0]?.voice || null;
}

export function unlockSpeech() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      if (!unlockSpeech._ctx) unlockSpeech._ctx = new Ctx();
      unlockSpeech._ctx.resume?.();
    }
  } catch (_) { /* ignore */ }
  try {
    if (!unlockSpeech._el) unlockSpeech._el = new Audio();
    const silent = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';
    unlockSpeech._el.src = silent;
    unlockSpeech._el.play().catch(() => {});
  } catch (_) { /* ignore */ }
  try {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  } catch (_) { /* ignore */ }
}

export function primedAudio() {
  if (typeof window === 'undefined') return null;
  if (!unlockSpeech._el) unlockSpeech._el = new Audio();
  return unlockSpeech._el;
}

export function speakWithBrowser(text, lang, { onEnd, onBoundary } = {}) {
  const spoken = String(text || '').trim();
  if (!spoken || typeof window === 'undefined' || !window.speechSynthesis) {
    onEnd?.();
    return () => {};
  }
  let cancelled = false;
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    onEnd?.();
  };
  const speakNow = () => {
    if (cancelled) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(spoken);
    utter.lang = browserVoiceLang(lang);
    utter.rate = 1.15;
    utter.pitch = 1;
    const match = pickBrowserVoice(window.speechSynthesis.getVoices?.() || [], lang);
    if (match) utter.voice = match;
    utter.onend = finish;
    utter.onerror = finish;
    if (onBoundary) utter.onboundary = onBoundary;
    window.speechSynthesis.speak(utter);
  };
  window.speechSynthesis.cancel();
  window.speechSynthesis.getVoices?.();
  const timer = window.setTimeout(speakNow, 180);
  const onVoices = () => {
    window.clearTimeout(timer);
    speakNow();
  };
  try { window.speechSynthesis.addEventListener?.('voiceschanged', onVoices, { once: true }); } catch (_) { /* ignore */ }
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
    try { window.speechSynthesis.removeEventListener?.('voiceschanged', onVoices); } catch (_) { /* ignore */ }
    try { window.speechSynthesis.cancel(); } catch (_) { /* ignore */ }
  };
}
